import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { DollarSign, Globe, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function ExchangeRateSettings() {
  const [rate, setRate] = useState('');
  const [published, setPublished] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings/exchange-rate').then(r => {
      setRate(r.data.rate > 0 ? String(r.data.rate) : '');
      setPublished(r.data.published || false);
      setUpdatedAt(r.data.updated_at || null);
    }).catch(() => {});
  }, []);

  const save = async () => {
    const rateNum = parseFloat(rate);
    if (!rate || isNaN(rateNum) || rateNum <= 0) {
      toast.error('Enter a valid exchange rate greater than 0');
      return;
    }
    setSaving(true);
    try {
      await api.put('/settings/exchange-rate', { rate: rateNum, published });
      toast.success(published ? 'Exchange rate published — all GHS amounts will show USD equivalents' : 'Exchange rate saved (not published)');
      setUpdatedAt(new Date().toISOString());
    } catch {
      toast.error('Failed to save exchange rate');
    }
    setSaving(false);
  };

  const minTopUp = rate && parseFloat(rate) > 0 ? (20 * parseFloat(rate)).toFixed(2) : null;

  return (
    <Card className="rounded-md border-slate-100">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Globe className="w-4 h-4 text-[#1976D2]" />
          USD / GHS Exchange Rate
          {published && <Badge className="bg-[#1976D2]/15 text-[#1976D2] text-[10px] ml-1">Live</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs text-slate-500 mb-1 block">1 USD equals</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">GHS</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={rate}
                onChange={e => setRate(e.target.value)}
                className="pl-12 h-9"
                placeholder="e.g. 15.50"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <DollarSign className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500">
              {rate && parseFloat(rate) > 0 ? `$1 = GHS ${parseFloat(rate).toFixed(2)}` : 'Set rate above'}
            </span>
          </div>
        </div>

        {minTopUp && (
          <p className="text-xs text-slate-400">
            Minimum student wallet top-up will be <span className="font-medium text-slate-600">GHS {minTopUp}</span> (~$20.00)
          </p>
        )}

        <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
          <div>
            <p className="text-sm font-medium text-slate-700">Publish rate</p>
            <p className="text-xs text-slate-400">When published, USD equivalents appear across all dashboards and set the minimum wallet top-up</p>
          </div>
          <button
            type="button"
            onClick={() => setPublished(p => !p)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${published ? 'bg-[#1976D2]' : 'bg-slate-200'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${published ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <div className="flex items-center justify-between">
          {updatedAt ? (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Last updated {new Date(updatedAt).toLocaleString()}
            </p>
          ) : <span />}
          <Button onClick={save} disabled={saving} className="h-8 text-xs px-4 bg-[#1565C0] hover:bg-[#0D47A1]">
            {saving ? 'Saving…' : published ? <><CheckCircle className="w-3 h-3 mr-1" />Publish Rate</> : 'Save Rate'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
